import { useEffect } from 'react'
import { View, Text } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Clock3, XCircle } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useToursCheckoutStore } from '@/lib/stores/tours-checkout.store'
import { parsePaymentReturnParams } from '@/lib/payments/return-params'
import { postThreeDsCompleteToOpener } from '@/lib/payments/three-ds-complete'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, SPACING } from '@/lib/brand'

type Outcome = 'success' | 'failed' | 'pending'

export default function ToursPaymentCompleteScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const params = useLocalSearchParams()
  const returned = parsePaymentReturnParams(params as Record<string, unknown>)
  const reset = useToursCheckoutStore((s) => s.reset)

  const rawStatus = Array.isArray(params.status) ? params.status[0] : params.status
  const status: Outcome =
    rawStatus === 'success'
      ? 'success'
      : rawStatus === 'failed' || returned.cancelled
        ? 'failed'
        : 'pending'

  useEffect(() => {
    postThreeDsCompleteToOpener({
      transactionId: returned.transactionId ?? undefined,
      rentalId: returned.rentalId ?? returned.bookingId ?? undefined,
      provider: returned.provider ?? undefined,
    })
    reset()
  }, [reset, returned.bookingId, returned.provider, returned.rentalId, returned.transactionId])

  const icon =
    status === 'success' ? (
      <CheckCircle2 size={56} color={colors.success} strokeWidth={1.6} />
    ) : status === 'failed' ? (
      <XCircle size={56} color={colors.textError} strokeWidth={1.6} />
    ) : (
      <Clock3 size={56} color={colors.textAccent} strokeWidth={1.6} />
    )

  return (
    <ScreenContainer>
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: SPACING['3xl'],
          paddingHorizontal: SPACING.lg,
        }}
      >
        {icon}
        <Text
          style={{
            marginTop: SPACING.lg,
            fontFamily: FONTS.bodyBold,
            fontSize: 22,
            color: colors.textPrimary,
            textAlign: 'center',
          }}
          testID={`tours.payment-complete-${status}`}
        >
          {t(`tours.checkout.${status}_title`)}
        </Text>
        <Text
          style={{
            marginTop: SPACING.sm,
            maxWidth: 360,
            fontFamily: FONTS.body,
            fontSize: 15,
            lineHeight: 22,
            color: colors.textSecondary,
            textAlign: 'center',
          }}
        >
          {t(`tours.checkout.${status}_body`)}
        </Text>
        <Button
          title={t('tours.checkout.back_to_tours')}
          size="lg"
          onPress={() => router.replace('/(tabs)/tours')}
          style={{ marginTop: SPACING.xl, minWidth: 240 }}
          testID="tours.payment-complete-back"
        />
        {status === 'success' ? (
          <Button
            title={t('tours.checkout.view_bookings')}
            variant="secondary"
            size="lg"
            onPress={() => router.replace('/(tabs)/tours/bookings')}
            style={{ marginTop: SPACING.sm, minWidth: 240 }}
            testID="tours.payment-complete-bookings"
          />
        ) : null}
      </View>
    </ScreenContainer>
  )
}
